import React from 'react';
import {
  Box,
  BoxProps,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  LeftChevron,
  SearchIcon,
  SortAscIcon,
  SortDescIcon,
} from 'components/SVG';
import Pagination from 'sharedComponents/Pagination';
import { useResize } from 'hooks/common';

type AlignmentOptions = BoxProps['alignItems'];
export type Column<TRow extends Row> = {
  field: string;
  headerText: string;
  sortable?: boolean;
  filterAble?: boolean;
  flex?: number;
  alignItems?: AlignmentOptions;
  justifyContent?: AlignmentOptions;
  cellRenderer?: (cellValue: TRow) => React.ReactNode;
  comparator?: (a: TRow, b: TRow) => number;
};

type Row = {
  id: string;
} & Record<string, string>;

type SparkDataTablePropsType<TRow extends Row> = {
  columns: Column<TRow>[];
  rows: TRow[];
  searchFilter?: boolean;
  searchPlaceholder?: string;
  title?: string;
  onRowClick?: (rowID: string) => void;
  pagination?: boolean;
  itemsPerPage?: number;
  shouldRenderHeader?: boolean;
  isRowClickable?: boolean;
  showLeftChevron?: boolean;
  onPageChange?: (pageNumber: number, pageSize: number) => void;
  defaultSortOrder?: SortFilter;
};

type SortFilter = {
  field: string;
  dir: 'asc' | 'desc';
};

const SparkDataTable = <TRow extends Row>({
  columns,
  rows,
  searchFilter = false,
  searchPlaceholder = '',
  title,
  onRowClick,
  pagination = false,
  itemsPerPage = 5,
  shouldRenderHeader = true,
  isRowClickable = true,
  showLeftChevron = true,
  onPageChange,
  defaultSortOrder,
}: SparkDataTablePropsType<TRow>) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [queryString, setQueryString] = React.useState('');
  const shouldPaginate = pagination && itemsPerPage;
  const [sortOrder, setSortOrder] = React.useState<SortFilter | null>(
    defaultSortOrder || null
  );
  const { isMobile } = useResize();

  React.useEffect(() => {
    if (!sortOrder && !!columns.length) {
      setSortOrder({
        field: columns[0]?.field,
        dir: 'asc',
      });
    }
  }, [columns, sortOrder]);

  const queryFilteredRows = React.useMemo(() => {
    if (!searchFilter) {
      return rows;
    }
    const filterableColumns = columns
      .filter((col) => col.filterAble)
      .map((col) => col.field);
    if (!filterableColumns.length) {
      return rows;
    }
    return rows.filter((row) => {
      return filterableColumns.some((filterableColumn) =>
        row[filterableColumn]
          ?.toLocaleLowerCase()
          .includes(queryString.toLocaleLowerCase())
      );
    });
  }, [columns, queryString, rows, searchFilter]);

  const paginatedResult = React.useMemo(() => {
    if (!shouldPaginate) {
      return queryFilteredRows;
    }
    const firstPageIndex = (currentPage - 1) * itemsPerPage;
    const lastPageIndex = firstPageIndex + itemsPerPage;
    return queryFilteredRows.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, itemsPerPage, queryFilteredRows, shouldPaginate]);

  const sortedRows = React.useMemo(() => {
    if (!sortOrder?.field) {
      return paginatedResult;
    }

    const column = columns.find((col) => col.field === sortOrder.field);
    return paginatedResult.sort((a, b) => {
      const sortMultiplier = sortOrder.dir === 'asc' ? 1 : -1;
      if (column?.comparator) {
        return column.comparator(a, b) * sortMultiplier;
      }
      return (
        a[sortOrder.field].localeCompare(b[sortOrder.field]) * sortMultiplier
      );
    });
  }, [paginatedResult, sortOrder, columns]);

  return (
    <Grid container gap={2}>
      {title && (
        <Box
          display='flex'
          align-items='center'
          justifyContent='space-between'
          marginBottom={-1}
        >
          <Typography
            variant='h5'
            sx={{
              fontFamily: 'Mont-semibold',
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
      {searchFilter && (
        <TextField
          placeholder={searchPlaceholder}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #EEEEEE',
            borderRadius: '12px',
          }}
          autoComplete='off'
          fullWidth={true}
          onChange={(e) => setQueryString(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      {shouldRenderHeader && (
        <Grid
          item
          container
          md={12}
          alignItems='center'
          paddingX={2}
          marginBottom={-1}
        >
          {columns.map((col, index) => (
            <Box
              display='flex'
              flex={col.flex ? col.flex : index === 0 ? 1.2 : 1}
              key={index}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#404040',
                  fontFamily: 'Mont-SemiBold',
                  ml: index === 0 ? '3.5%' : 0,
                  cursor: col.sortable ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (col.sortable) {
                    setSortOrder({
                      field: col.field,
                      dir: sortOrder?.dir === 'asc' ? 'desc' : 'asc',
                    });
                  }
                }}
              >
                {col.headerText}
              </Typography>
              {col.sortable && sortOrder?.field === col.field && (
                <IconButton
                  sx={{ padding: 0, paddingLeft: 1 }}
                  onClick={() =>
                    setSortOrder({
                      field: col.field,
                      dir: sortOrder?.dir === 'asc' ? 'desc' : 'asc',
                    })
                  }
                >
                  {col.field === sortOrder?.field && sortOrder.dir === 'asc' ? (
                    <SortAscIcon width='12px' height='12px' />
                  ) : (
                    <SortDescIcon width='12px' height='12px' />
                  )}
                </IconButton>
              )}
            </Box>
          ))}
        </Grid>
      )}
      {sortedRows.map((row, index) => (
        <Grid
          key={index}
          component='div'
          onClick={() => onRowClick && onRowClick(row.id)}
          item
          container
          md={12}
          justifyContent='center'
          alignItems='center'
          flexWrap='nowrap'
          padding={2}
          bgcolor={(theme) => theme.palette.common.white}
          border={(theme) => `1px solid ${theme.palette.borderColor}`}
          borderRadius='10px'
          position='relative'
          sx={
            isRowClickable
              ? {
                  transition: 'transform 0.1s',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                  '&:active': { transform: 'scale(0.99)' },
                }
              : {}
          }
        >
          {columns.map((col, index) => (
            <Box
              key={index}
              display='flex'
              flex={col.flex ? col.flex : index === 0 ? 1.2 : 1}
              alignItems={col.alignItems || 'center'}
              justifyContent={col.justifyContent || 'flex-start'}
            >
              {col.cellRenderer ? col.cellRenderer(row) : row[col.field]}
            </Box>
          ))}

          {!isMobile && isRowClickable && showLeftChevron && (
            <Box
              position='absolute'
              right={0}
              sx={{ transform: 'translate(-50%,-50%)' }}
              top='53%'
            >
              <LeftChevron />
            </Box>
          )}
        </Grid>
      ))}

      {shouldPaginate && rows.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          dataCount={queryFilteredRows.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Grid>
  );
};

export default SparkDataTable;
